<template>
    <div class="section mt-1 event-management-page">
        <div class="page-container">
            <div class="event-mgmt-toolbar" v-if="canCreate">
                <button type="button" class="btn btn-primary btn-create-event" @click="openCreateModal">
                    <ion-icon name="add-outline"></ion-icon>
                    <span>Create event</span>
                </button>
            </div>

            <div class="form-group basic event-search-field">
                <label class="label visually-hidden" for="event-mgmt-search">Search events</label>
                <div class="input-with-leading-icon">
                    <ion-icon name="search-outline" aria-hidden="true"></ion-icon>
                    <input id="event-mgmt-search" type="search" class="form-control" autocomplete="off"
                        placeholder="Search events…" v-model="search" @input="onSearch" />
                </div>
            </div>

            <!-- Loading -->
            <div class="section text-center mt-2" v-if="loading">
                <div class="spinner-border text-secondary" role="status"></div>
            </div>

            <!-- Empty State -->
            <div class="listed-detail mt-3" v-if="events.length === 0 && !loading">
                <h3 class="text-center mt-2 text-primary">No Events Found</h3>
            </div>

            <!-- Events List -->
            <div class="goals mt-1" v-if="!loading">
                <div class="meeting-grid">
                    <div class="meeting-card" v-for="event in events" :key="event.id">
                        <router-link :to="'/event-tracking/' + event.id" class="meeting-link">
                            <div class="in">
                                <div class="meeting-header-compact">
                                    <h3 class="meeting-title-compact">{{ event.name }}</h3>
                                    <span class="badge" :class="levelBadgeClass(event.event_level)">
                                        {{ formatLevel(event.event_level) }}
                                    </span>
                                </div>
                                <div class="meeting-info-compact mt-1">
                                    <span class="info-item" v-if="event.date_from">
                                        <ion-icon name="calendar-outline"></ion-icon>
                                        {{ event.date_from }}
                                        <span v-if="event.date_to"> – {{ event.date_to }}</span>
                                    </span>
                                    <span class="info-item" v-else-if="event.date">
                                        <ion-icon name="calendar-outline"></ion-icon>
                                        {{ event.date }}
                                    </span>
                                    <span class="info-separator" v-if="event.org_level">/</span>
                                    <span class="info-item" v-if="event.org_level">
                                        <ion-icon name="people-outline"></ion-icon>
                                        {{ formatOrgLevel(event.org_level) }}
                                    </span>
                                </div>
                                <div class="meeting-location-compact" v-if="event.place">
                                    <ion-icon name="location-outline"></ion-icon>
                                    <span>{{ event.place }}</span>
                                </div>
                            </div>
                            <ion-icon name="chevron-forward-outline"></ion-icon>
                        </router-link>
                    </div>
                </div>

                <!-- Pagination -->
                <div class="pagination-row mt-2" v-if="lastPage > 1">
                    <button class="btn btn-outline-primary btn-sm" :disabled="currentPage === 1"
                        @click="fetchEvents(currentPage - 1)">
                        <ion-icon name="chevron-back-outline"></ion-icon> Prev
                    </button>
                    <span class="text-muted small">{{ currentPage }} / {{ lastPage }}</span>
                    <button class="btn btn-outline-primary btn-sm" :disabled="currentPage === lastPage"
                        @click="fetchEvents(currentPage + 1)">
                        Next <ion-icon name="chevron-forward-outline"></ion-icon>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Create Event — bottom action sheet (app standard) -->
    <div class="modal fade action-sheet" id="createEventModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Create event</h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content create-event-sheet">
                        <form @submit.prevent="submitCreate">
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Event name <span class="text-danger">*</span></label>
                                    <input type="text" class="form-control" v-model="form.name" required />
                                </div>
                            </div>

                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Description</label>
                                    <textarea class="form-control" rows="2" v-model="form.description"></textarea>
                                </div>
                            </div>

                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Level <span class="text-danger">*</span></label>
                                    <select class="form-control form-select" v-model="form.event_level" required>
                                        <option value="">Select level</option>
                                        <option value="state" v-if="ctx.user_level === 1">State</option>
                                        <option value="district" v-if="ctx.user_level <= 2">District / All Districts</option>
                                        <option value="mandalam" v-if="ctx.user_level <= 3">Mandalam / All Mandalam</option>
                                        <option value="unit" v-if="ctx.user_level <= 4">Unit / All Units</option>
                                    </select>
                                </div>
                            </div>

                            <!-- Sub level selectors -->
                            <div class="form-group basic" v-if="form.event_level === 'district' && ctx.user_level === 1">
                                <div class="input-wrapper">
                                    <label class="label">District</label>
                                    <select class="form-control form-select" v-model="form.district_id">
                                        <option value="">All Districts</option>
                                        <option v-for="d in districts" :key="'d-'+d.id" :value="d.id">{{ d.name }}</option>
                                    </select>
                                </div>
                            </div>

                            <div class="form-group basic" v-if="form.event_level === 'mandalam' && ctx.user_level <= 2">
                                <div class="input-wrapper">
                                    <label class="label">District</label>
                                    <select class="form-control form-select"
                                        v-model="form.district_id"
                                        :disabled="ctx.user_level === 2">
                                        <option value="">{{ ctx.user_level === 1 ? 'All Districts' : 'Selected district' }}</option>
                                        <template v-for="d in districts" :key="'md-'+d.id">
                                            <option v-if="ctx.user_level === 1" :value="d.id">{{ d.name }}</option>
                                        </template>
                                    </select>
                                    <div class="text-muted small mt-1" v-if="ctx.user_level === 2">
                                        District is fixed by your login level.
                                    </div>
                                </div>
                            </div>

                            <div class="form-group basic" v-if="form.event_level === 'mandalam' && ctx.user_level <= 2">
                                <div class="input-wrapper">
                                    <label class="label">Mandalam</label>
                                    <select class="form-control form-select" v-model="form.zone_id">
                                        <option value="">All Mandalam</option>
                                        <option v-for="z in zones" :key="'z-'+z.id" :value="z.id">{{ z.name }}</option>
                                    </select>
                                </div>
                            </div>

                            <div class="form-group basic" v-if="form.event_level === 'unit' && ctx.user_level <= 3">
                                <div class="input-wrapper">
                                    <label class="label">District</label>
                                    <select class="form-control form-select"
                                        v-model="form.district_id"
                                        :disabled="ctx.user_level === 2 || ctx.user_level === 3">
                                        <option value="">{{ ctx.user_level === 1 ? 'All Districts' : 'Selected district' }}</option>
                                        <template v-for="d in districts" :key="'ud-'+d.id">
                                            <option v-if="ctx.user_level === 1" :value="d.id">{{ d.name }}</option>
                                        </template>
                                    </select>
                                </div>
                            </div>

                            <div class="form-group basic" v-if="form.event_level === 'unit' && ctx.user_level <= 3">
                                <div class="input-wrapper">
                                    <label class="label">Mandalam</label>
                                    <select class="form-control form-select"
                                        v-model="form.zone_id"
                                        :disabled="ctx.user_level === 3">
                                        <option value="">{{ ctx.user_level === 1 || ctx.user_level === 2 ? 'All Mandalam' : 'Selected mandalam' }}</option>
                                        <template v-for="z in zones" :key="'uz-'+z.id">
                                            <option v-if="ctx.user_level !== 3" :value="z.id">{{ z.name }}</option>
                                        </template>
                                    </select>
                                    <div class="text-muted small mt-1" v-if="ctx.user_level === 3">
                                        Mandalam is fixed by your login level.
                                    </div>
                                </div>
                            </div>

                            <div class="form-group basic" v-if="form.event_level === 'unit' && ctx.user_level <= 4">
                                <div class="input-wrapper">
                                    <label class="label">Unit</label>
                                    <select class="form-control form-select"
                                        v-model="form.unit_id"
                                        :disabled="ctx.user_level === 4">
                                        <option value="">{{ ctx.user_level > 3 ? 'Selected unit' : 'All Units' }}</option>
                                        <template v-for="u in units" :key="'u-'+u.id">
                                            <option v-if="ctx.user_level !== 4" :value="u.id">{{ u.name }}</option>
                                        </template>
                                    </select>
                                    <div class="text-muted small mt-1" v-if="ctx.user_level === 4">
                                        Unit is fixed by your login level.
                                    </div>
                                </div>
                            </div>

                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Organisation <span class="text-danger">*</span></label>
                                    <select class="form-control form-select" v-model="form.org_level" required>
                                        <option value="">Select organisation</option>
                                        <option value="organisation">Organisation</option>
                                        <option value="youth">Youth</option>
                                        <option value="students">Students</option>
                                        <option value="women">Women</option>
                                        <option value="girls">Girls</option>
                                        <option value="balavedi">Balavedi</option>
                                        <option value="all">ALL</option>
                                    </select>
                                </div>
                            </div>

                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Date type</label>
                                    <select class="form-control form-select" v-model="form.date_type">
                                        <option value="single">Single date & time</option>
                                        <option value="range">Date range</option>
                                        <option value="lower_level">Fixed by lower level</option>
                                    </select>
                                </div>
                            </div>

                            <template v-if="form.date_type === 'single'">
                                <div class="form-group basic">
                                    <div class="input-wrapper">
                                        <label class="label">Date</label>
                                        <input type="date" class="form-control" v-model="form.date" />
                                    </div>
                                </div>
                                <div class="form-group basic">
                                    <div class="input-wrapper">
                                        <label class="label">Time</label>
                                        <input type="time" class="form-control" v-model="form.time" />
                                    </div>
                                </div>
                            </template>

                            <template v-if="form.date_type === 'range'">
                                <div class="form-group basic">
                                    <div class="input-wrapper">
                                        <label class="label">From</label>
                                        <input type="date" class="form-control" v-model="form.date_from" />
                                    </div>
                                </div>
                                <div class="form-group basic">
                                    <div class="input-wrapper">
                                        <label class="label">To</label>
                                        <input type="date" class="form-control" v-model="form.date_to" />
                                    </div>
                                </div>
                            </template>

                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label">Place / venue</label>
                                    <input type="text" class="form-control" v-model="form.place" />
                                </div>
                            </div>

                            <div class="alert alert-danger py-2 px-3 small" v-if="createError">{{ createError }}</div>

                            <div class="form-group basic mb-0">
                                <button type="submit" class="btn btn-primary btn-block btn-lg" :disabled="submitting">
                                    <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
                                    Create event
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import $ from "jquery";
import UserService from "../../services/user.service";

export default {
    name: "EventManagement",
    data() {
        return {
            loading: false,
            events: [],
            currentPage: 1,
            lastPage: 1,
            search: "",
            searchTimer: null,
            canCreate: true,

            form: {
                name: "",
                description: "",
                event_level: "",
                org_level: "",
                date_type: "single",
                date: "",
                time: "",
                date_from: "",
                date_to: "",
                place: "",
                district_id: "",
                zone_id: "",
                unit_id: "",
            },
            submitting: false,
            createError: "",

            ctx: { user_level: 9, district_id: null, zone_id: null, unit_id: null },
            districts: [],
            zones: [],
            units: [],
        };
    },
    mounted() {
        this.loadContext();
        this.fetchEvents(1);
    },
    methods: {
        loadContext() {
            // Load user context (level + fixed org IDs)
            UserService.authPostRequest("event-tracking-context", {}).then((res) => {
                if (res.data.status === "success") {
                    this.ctx = res.data.data;
                }
            });
            // Preload districts list for state admin
            UserService.authPostRequest("event-tracking-districts", {}).then((res) => {
                if (res.data.status === "success") this.districts = res.data.data.districts || [];
            });
        },

        fetchZones() {
            const payload = { district_id: this.form.district_id || null };
            // If district is fixed by login, always use ctx.district_id
            if (this.ctx.user_level === 2 && this.ctx.district_id) payload.district_id = this.ctx.district_id;
            UserService.authPostRequest("event-tracking-zones", payload).then((res) => {
                if (res.data.status === "success") this.zones = res.data.data.zones || [];
            });
        },

        fetchUnits() {
            const payload = {
                district_id: this.form.district_id || null,
                zone_id: this.form.zone_id || null,
            };
            if (this.ctx.user_level === 2 && this.ctx.district_id) payload.district_id = this.ctx.district_id;
            if (this.ctx.user_level === 3 && this.ctx.zone_id) payload.zone_id = this.ctx.zone_id;
            UserService.authPostRequest("event-tracking-units", payload).then((res) => {
                if (res.data.status === "success") this.units = res.data.data.units || [];
            });
        },

        fetchEvents(page = 1) {
            this.loading = true;
            UserService.authPostRequest("get-event-tracking-list", {
                page: page,
                search: this.search,
            }).then(
                (response) => {
                    if (response.data.status === "success") {
                        this.events = response.data.data.events;
                        this.currentPage = response.data.data.current_page;
                        this.lastPage = response.data.data.last_page;
                    }
                    this.loading = false;
                },
                () => { this.loading = false; }
            );
        },

        onSearch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.fetchEvents(1), 400);
        },

        openCreateModal() {
            this.form = {
                name: "", description: "", event_level: "", org_level: "",
                date_type: "single", date: "", time: "", date_from: "", date_to: "", place: "",
                district_id: "", zone_id: "", unit_id: "",
            };
            this.createError = "";
            $("#createEventModal").modal("show");
        },

        submitCreate() {
            this.submitting = true;
            this.createError = "";

            // Build org scope ids
            let district_id = null, zone_id = null, unit_id = null;
            if (this.form.event_level === "district") {
                district_id = this.ctx.user_level === 2 ? this.ctx.district_id : (this.form.district_id || null);
            }
            if (this.form.event_level === "mandalam") {
                district_id = this.ctx.user_level === 2 ? this.ctx.district_id : (this.form.district_id || null);
                zone_id     = this.ctx.user_level === 3 ? this.ctx.zone_id : (this.form.zone_id || null);
            }
            if (this.form.event_level === "unit") {
                district_id = this.ctx.user_level === 2 ? this.ctx.district_id : (this.form.district_id || null);
                zone_id     = this.ctx.user_level === 3 ? this.ctx.zone_id : (this.form.zone_id || null);
                unit_id     = this.ctx.user_level === 4 ? this.ctx.unit_id : (this.form.unit_id || null);
            }

            const payload = {
                name: this.form.name,
                description: this.form.description,
                event_level: this.form.event_level,
                org_level: this.form.org_level,
                place: this.form.place,
                district_id,
                zone_id,
                unit_id,
                date_fixed_by_lower_level: this.form.date_type === "lower_level" ? 1 : 0,
                date: this.form.date_type === "single" ? this.form.date : null,
                time: this.form.date_type === "single" ? this.form.time : null,
                date_from: this.form.date_type === "range" ? this.form.date_from : null,
                date_to: this.form.date_type === "range" ? this.form.date_to : null,
            };

            UserService.authPostRequest("create-event-tracking", payload).then(
                (response) => {
                    this.submitting = false;
                    if (response.data.status === "success") {
                        $("#createEventModal").modal("hide");
                        const event = response.data.data.event;
                        this.$router.push("/event-tracking/" + event.id);
                    } else {
                        this.createError = response.data.message || "Failed to create event";
                    }
                },
                (error) => {
                    this.submitting = false;
                    this.createError = error.response?.data?.message || "An error occurred";
                }
            );
        },

        formatLevel(level) {
            const map = {
                state: "State", district: "District",
                mandalam: "Mandalam", unit: "Unit",
            };
            return map[level] || level;
        },

        formatOrgLevel(level) {
            const map = {
                organisation: "Organisation", youth: "Youth", students: "Students",
                women: "Women", girls: "Girls", balavedi: "Balavedi", all: "ALL",
            };
            return map[level] || level;
        },

        levelBadgeClass(level) {
            const map = {
                state: "bg-danger", district: "bg-primary",
                mandalam: "bg-success", unit: "bg-warning text-dark",
            };
            return map[level] || "bg-secondary";
        },
    },
    watch: {
        "form.event_level"(val) {
            // Reset scope fields on level change and apply fixed IDs by login level
            this.form.district_id = "";
            this.form.zone_id = "";
            this.form.unit_id = "";
            this.zones = [];
            this.units = [];

            if (val === "district" && this.ctx.user_level === 2 && this.ctx.district_id) {
                this.form.district_id = this.ctx.district_id;
            }
            if ((val === "mandalam" || val === "unit") && this.ctx.user_level === 2 && this.ctx.district_id) {
                this.form.district_id = this.ctx.district_id;
                this.fetchZones();
            }
            if (val === "unit" && this.ctx.user_level === 3 && this.ctx.zone_id) {
                this.form.zone_id = this.ctx.zone_id;
                this.fetchUnits();
            }
            if (val === "unit" && this.ctx.user_level === 4 && this.ctx.unit_id) {
                this.form.unit_id = this.ctx.unit_id;
            }
        },
        "form.district_id"() {
            if (this.form.event_level === "mandalam" || this.form.event_level === "unit") {
                this.form.zone_id = "";
                this.form.unit_id = "";
                this.units = [];
                this.fetchZones();
            }
        },
        "form.zone_id"() {
            if (this.form.event_level === "unit") {
                this.form.unit_id = "";
                this.fetchUnits();
            }
        }
    },
};
</script>

<style scoped>
.event-management-page {
    padding-bottom: 12px;
    --page-pad: 16px;
}

.page-container {
    width: 100%;
    max-width: 820px;
    margin: 0 auto;
    padding: 0 var(--page-pad);
}

.event-mgmt-toolbar {
    padding: 0 0 12px;
}

.btn-create-event {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
    font-weight: 600;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.btn-create-event ion-icon {
    font-size: 22px;
}

.event-search-field {
    padding: 0;
    margin-bottom: 4px;
}

.input-with-leading-icon {
    position: relative;
}

.input-with-leading-icon ion-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 20px;
    color: #8e8e93;
    pointer-events: none;
}

.input-with-leading-icon .form-control {
    padding-left: 44px;
    min-height: 44px;
    border-radius: 10px;
}

#createEventModal .create-event-sheet {
    max-height: min(78vh, 560px);
    padding-bottom: 8px;
}

.meeting-grid { display: flex; flex-direction: column; gap: 10px; padding: 0 16px; }
.meeting-grid { padding: 0; }
.meeting-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    border: 1px solid #eef0f3;
}
.meeting-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    text-decoration: none;
    color: inherit;
}
.meeting-header-compact {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}
.meeting-title-compact {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0;
    flex: 1;
    line-height: 1.3;
    line-clamp: 2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.meeting-info-compact { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 4px; }
.info-item { display: flex; align-items: center; gap: 3px; font-size: 12px; color: #6c757d; }
.info-separator { color: #ccc; }
.meeting-location-compact { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #6c757d; }

.pagination-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

:deep(.modal-header) {
    align-items: center;
}
:deep(.close-button) {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    border: 1px solid #e9ecef;
    background: #f8f9fb;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-size: 20px;
}
:deep(.close-button:active) { transform: scale(0.98); }

@media (max-width: 420px) {
    .event-management-page { --page-pad: 12px; }
    .meeting-link { padding: 12px 12px; }
    .input-with-leading-icon ion-icon { left: 12px; }
    .input-with-leading-icon .form-control { padding-left: 42px; }
}
</style>
